import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, onSnapshot, doc, updateDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import { FaCashRegister, FaChild, FaCoffee, FaTicketAlt, FaClock, FaCheckCircle, FaTimes, FaMoneyBillWave } from 'react-icons/fa';
import Modal from 'react-modal';
import { ClipLoader } from 'react-spinners';

const formatCurrency = (value) => {
    const number = Number(value);
    if (isNaN(number)) return '0,00 €';
    return number.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const cardStyle = "bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100";
const inputStyle = "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-kokimiko-teal focus:outline-none text-sm font-bold text-kokimiko-texto";
const labelStyle = "block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2";

const customModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        marginRight: '-50%', transform: 'translate(-50%, -50%)',
        border: 'none', background: 'transparent', width: '100%', maxWidth: '500px', padding: '0'
    },
    overlay: { backgroundColor: 'rgba(35, 37, 89, 0.75)', zIndex: 1000 }
};

// Tarifas de Kokimiko (Basadas en el PDF)
const TARIFAS = {
    '1_hora': { id: '1_hora', nombre: '1 Hora de Juego Libre', precio: 8.50, color: 'text-kokimiko-teal', bg: 'bg-kokimiko-teal/10' },
    '2_horas': { id: '2_horas', nombre: '2 Horas de Juego Libre', precio: 14.00, color: 'text-kokimiko-azul', bg: 'bg-kokimiko-azul/10' },
    'bono': { id: 'bono', nombre: 'Descontar de Bono (10h)', precio: 0.00, color: 'text-purple-600', bg: 'bg-purple-100' },
    'cafeteria': { id: 'cafeteria', nombre: 'Venta Cafetería / Tienda', precio: 0.00, color: 'text-orange-600', bg: 'bg-orange-100' }
};

function MostradorView() {
    const [sesiones, setSesiones] = useState([]);
    const [ventasDia, setVentasDia] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [tipoVentaSeleccionada, setTipoVentaSeleccionada] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Estado del Formulario Rápido
    const [formData, setFormData] = useState({
        nombreNino: '',
        nombreTutor: '',
        metodoPago: 'Tarjeta',
        montoPersonalizado: ''
    });

    // Cargar datos del día en tiempo real
    useEffect(() => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        // Sesiones Activas y de hoy
        const qSesiones = query(collection(db, "mostrador_sesiones"), where("createdAt", ">=", hoy), orderBy("createdAt", "desc"));
        const qVentas = query(collection(db, "mostrador_ventas"), where("fecha", ">=", hoy));

        const unsubSesiones = onSnapshot(qSesiones, (snap) => {
            setSesiones(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setCargando(false);
        });

        const unsubVentas = onSnapshot(qVentas, (snap) => {
            setVentasDia(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubSesiones(); unsubVentas(); };
    }, []);

    const abrirModalVenta = (tipoId) => {
        setTipoVentaSeleccionada(TARIFAS[tipoId]);
        setFormData({ nombreNino: '', nombreTutor: '', metodoPago: 'Tarjeta', montoPersonalizado: '' });
        setModalAbierto(true);
    };

    const handleProcesarVenta = async (e) => {
        e.preventDefault();
        
        // Validación básica
        if (tipoVentaSeleccionada.id !== 'cafeteria' && !formData.nombreNino.trim()) {
            return toast.error("Ingresa el nombre del niño.");
        }
        
        setIsSaving(true);
        const toastId = toast.loading('Procesando ticket...');

        try {
            const precioFinal = tipoVentaSeleccionada.id === 'cafeteria' 
                ? parseFloat(formData.montoPersonalizado) || 0 
                : tipoVentaSeleccionada.precio;

            // 1. Registrar el ingreso financiero
            await addDoc(collection(db, "mostrador_ventas"), {
                concepto: tipoVentaSeleccionada.nombre,
                monto: precioFinal,
                metodoPago: formData.metodoPago,
                fecha: new Date(),
                tipoId: tipoVentaSeleccionada.id
            });

            // 2. Si es juego libre, registrar la sesión para control de sala
            if (tipoVentaSeleccionada.id !== 'cafeteria') {
                await addDoc(collection(db, "mostrador_sesiones"), {
                    nombreNino: formData.nombreNino,
                    nombreTutor: formData.nombreTutor,
                    tarifaId: tipoVentaSeleccionada.id,
                    tarifaNombre: tipoVentaSeleccionada.nombre,
                    estado: 'En sala',
                    createdAt: serverTimestamp(),
                    horaEntrada: new Date().toISOString()
                });
            }

            toast.success('¡Operación registrada!', { id: toastId });
            setModalAbierto(false);
        } catch (error) {
            console.error(error);
            toast.error('Error al procesar la venta.', { id: toastId });
        } finally {
            setIsSaving(false);
        }
    };

    const handleMarcarSalida = async (sesionId) => {
        try {
            await updateDoc(doc(db, "mostrador_sesiones", sesionId), {
                estado: 'Completado',
                horaSalida: new Date().toISOString()
            });
            toast.success('Salida registrada.');
        } catch (error) {
            toast.error('Error al actualizar el estado.');
        }
    };

    // Cálculos de Caja
    const totalRecaudado = ventasDia.reduce((acc, v) => acc + v.monto, 0);
    const totalTarjeta = ventasDia.filter(v => v.metodoPago === 'Tarjeta').reduce((acc, v) => acc + v.monto, 0);
    const totalEfectivo = ventasDia.filter(v => v.metodoPago === 'Efectivo').reduce((acc, v) => acc + v.monto, 0);
    const totalBizum = ventasDia.filter(v => v.metodoPago === 'Bizum').reduce((acc, v) => acc + v.monto, 0);
    
    const sesionesActivas = sesiones.filter(s => s.estado === 'En sala');

    if (cargando) return <div className="flex justify-center items-center h-full"><ClipLoader color="#1BB1A5" size={50} /></div>;

    return (
        <div className="h-full flex flex-col p-4 md:p-8 gap-6 bg-kokimiko-fondo/30 w-full overflow-x-hidden font-sans">
            <Toaster position="top-center" />
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-4 bg-kokimiko-teal rounded-2xl text-white shadow-md"><FaCashRegister size={24} /></div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-kokimiko-texto uppercase tracking-widest">Mostrador & Caja Diaria</h1>
                        <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-widest">{new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                </div>
            </div>

            {/* BOTONES RÁPIDOS (TPV) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button onClick={() => abrirModalVenta('1_hora')} className={`${cardStyle} flex flex-col items-center justify-center p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-kokimiko-teal group`}>
                    <div className={`p-4 rounded-full ${TARIFAS['1_hora'].bg} ${TARIFAS['1_hora'].color} mb-3 group-hover:scale-110 transition-transform`}><FaChild size={24}/></div>
                    <span className="font-black text-kokimiko-texto text-sm uppercase tracking-widest text-center">{TARIFAS['1_hora'].nombre}</span>
                    <span className="font-bold text-kokimiko-teal text-lg mt-1">{formatCurrency(TARIFAS['1_hora'].precio)}</span>
                </button>
                
                <button onClick={() => abrirModalVenta('2_horas')} className={`${cardStyle} flex flex-col items-center justify-center p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-kokimiko-azul group`}>
                    <div className={`p-4 rounded-full ${TARIFAS['2_horas'].bg} ${TARIFAS['2_horas'].color} mb-3 group-hover:scale-110 transition-transform`}><FaClock size={24}/></div>
                    <span className="font-black text-kokimiko-texto text-sm uppercase tracking-widest text-center">{TARIFAS['2_horas'].nombre}</span>
                    <span className="font-bold text-kokimiko-azul text-lg mt-1">{formatCurrency(TARIFAS['2_horas'].precio)}</span>
                </button>

                <button onClick={() => abrirModalVenta('bono')} className={`${cardStyle} flex flex-col items-center justify-center p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-purple-400 group`}>
                    <div className={`p-4 rounded-full ${TARIFAS['bono'].bg} ${TARIFAS['bono'].color} mb-3 group-hover:scale-110 transition-transform`}><FaTicketAlt size={24}/></div>
                    <span className="font-black text-kokimiko-texto text-sm uppercase tracking-widest text-center">{TARIFAS['bono'].nombre}</span>
                    <span className="font-bold text-purple-600 text-lg mt-1">Sin cobro (0€)</span>
                </button>

                <button onClick={() => abrirModalVenta('cafeteria')} className={`${cardStyle} flex flex-col items-center justify-center p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-orange-400 group`}>
                    <div className={`p-4 rounded-full ${TARIFAS['cafeteria'].bg} ${TARIFAS['cafeteria'].color} mb-3 group-hover:scale-110 transition-transform`}><FaCoffee size={24}/></div>
                    <span className="font-black text-kokimiko-texto text-sm uppercase tracking-widest text-center">{TARIFAS['cafeteria'].nombre}</span>
                    <span className="font-bold text-orange-600 text-lg mt-1">Monto Variable</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
                {/* TABLA DE SALA (CONTROL DE NIÑOS) */}
                <div className={`${cardStyle} lg:col-span-2 flex flex-col`}>
                    <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                        <h3 className="font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-2">
                            <span className="relative flex h-3 w-3 mr-2">
                              {sesionesActivas.length > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-kokimiko-teal opacity-75"></span>}
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-kokimiko-teal"></span>
                            </span>
                            Control de Sala: En Juego ({sesionesActivas.length})
                        </h3>
                    </div>
                    
                    <div className="overflow-x-auto custom-scrollbar flex-1">
                        <table className="w-full text-sm text-left">
                            <thead className="text-[10px] text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100 font-black">
                                <tr>
                                    <th className="px-4 py-3 rounded-tl-xl">Hora Entrada</th>
                                    <th className="px-4 py-3">Nombre del Niño (Tutor)</th>
                                    <th className="px-4 py-3 text-center">Modalidad</th>
                                    <th className="px-4 py-3 text-center rounded-tr-xl">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sesionesActivas.length > 0 ? (
                                    sesionesActivas.map(sesion => {
                                        const horaEntrada = sesion.horaEntrada ? new Date(sesion.horaEntrada).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '---';
                                        return (
                                            <tr key={sesion.id} className="border-b border-gray-50 hover:bg-kokimiko-azul/5">
                                                <td className="px-4 py-3 font-black text-kokimiko-texto">{horaEntrada}</td>
                                                <td className="px-4 py-3">
                                                    <p className="font-black text-kokimiko-texto text-base">{sesion.nombreNino}</p>
                                                    {sesion.nombreTutor && <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{sesion.nombreTutor}</p>}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md inline-block ${sesion.tarifaId === 'bono' ? 'bg-purple-100 text-purple-600' : 'bg-kokimiko-teal/10 text-kokimiko-teal'}`}>
                                                        {sesion.tarifaNombre}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <button onClick={() => handleMarcarSalida(sesion.id)} className="bg-kokimiko-texto text-white p-2 px-4 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-red-500 transition-colors">
                                                        Retirar / Salida
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr><td colSpan="4" className="text-center py-12 text-gray-400 font-bold text-xs uppercase tracking-widest">No hay niños jugando ahora mismo.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* RESUMEN DE CAJA (DERECHA) */}
                <div className={`${cardStyle} flex flex-col bg-gray-50`}>
                    <h3 className="font-black text-kokimiko-texto mb-6 uppercase tracking-widest border-b border-gray-200 pb-4 flex items-center gap-2">
                        <FaMoneyBillWave className="text-kokimiko-teal"/> Cuadre de Caja Diario
                    </h3>
                    
                    <div className="space-y-4 mb-6">
                        <div className="flex justify-between items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Efectivo</span>
                            <span className="font-black text-gray-700">{formatCurrency(totalEfectivo)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Tarjeta (TPV)</span>
                            <span className="font-black text-gray-700">{formatCurrency(totalTarjeta)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Bizum</span>
                            <span className="font-black text-gray-700">{formatCurrency(totalBizum)}</span>
                        </div>
                    </div>

                    <div className="mt-auto bg-kokimiko-texto p-6 rounded-2xl text-white text-center shadow-lg">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-300 mb-1">Recaudación Total Hoy</p>
                        <p className="text-3xl font-black text-kokimiko-teal">{formatCurrency(totalRecaudado)}</p>
                        <p className="text-xs font-bold text-gray-400 mt-2">{ventasDia.length} Operaciones procesadas</p>
                    </div>
                </div>
            </div>

            {/* MODAL DE COBRO RÁPIDO */}
            <Modal isOpen={modalAbierto} onRequestClose={() => setModalAbierto(false)} style={customModalStyles}>
                <form onSubmit={handleProcesarVenta} className="bg-white p-8 rounded-[2rem] shadow-2xl font-sans">
                    <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                        <h3 className={`text-xl font-black uppercase tracking-widest flex items-center gap-2 ${tipoVentaSeleccionada?.color}`}>
                            {tipoVentaSeleccionada?.nombre}
                        </h3>
                        <button type="button" onClick={() => setModalAbierto(false)} className="text-gray-400 hover:text-red-500 transition-colors p-2"><FaTimes size={20}/></button>
                    </div>

                    <div className="space-y-4 mb-8">
                        {tipoVentaSeleccionada?.id !== 'cafeteria' && (
                            <>
                                <div>
                                    <label className={labelStyle}>Nombre del Niño(a) *</label>
                                    <input type="text" value={formData.nombreNino} onChange={e => setFormData({...formData, nombreNino: e.target.value})} className={inputStyle} required placeholder="Ej: Leo Fernández" autoFocus/>
                                </div>
                                <div>
                                    <label className={labelStyle}>Tutor / Responsable (Opcional)</label>
                                    <input type="text" value={formData.nombreTutor} onChange={e => setFormData({...formData, nombreTutor: e.target.value})} className={inputStyle} placeholder="Ej: Madre Elena"/>
                                </div>
                            </>
                        )}

                        {tipoVentaSeleccionada?.id === 'cafeteria' && (
                            <div>
                                <label className={labelStyle}>Monto Total a Cobrar (€) *</label>
                                <input type="number" step="0.01" min="0.1" value={formData.montoPersonalizado} onChange={e => setFormData({...formData, montoPersonalizado: e.target.value})} className={`${inputStyle} text-xl`} required placeholder="0.00" autoFocus/>
                            </div>
                        )}

                        {tipoVentaSeleccionada?.id !== 'bono' && (
                            <div>
                                <label className={labelStyle}>Método de Pago</label>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    {['Tarjeta', 'Efectivo', 'Bizum'].map(metodo => (
                                        <button 
                                            key={metodo} type="button" 
                                            onClick={() => setFormData({...formData, metodoPago: metodo})}
                                            className={`p-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${formData.metodoPago === metodo ? 'bg-kokimiko-teal text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                        >
                                            {metodo}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-4">
                        <button type="button" onClick={() => setModalAbierto(false)} className="w-1/3 bg-gray-100 text-gray-500 font-black text-xs uppercase tracking-widest rounded-xl hover:bg-gray-200" disabled={isSaving}>Cancelar</button>
                        <button type="submit" className="w-2/3 bg-kokimiko-texto text-white font-black text-sm uppercase tracking-widest h-14 rounded-xl hover:opacity-80 flex items-center justify-center gap-2" disabled={isSaving}>
                            <FaCheckCircle /> {isSaving ? 'Registrando...' : (tipoVentaSeleccionada?.id === 'bono' ? 'Confirmar Acceso' : 'Cobrar e Ingresar')}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

export default MostradorView;